import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Banner, Button, ButtonLink, Card, Dialog, Field, FileDrop, Money, PageHeader, Pill, type LinkComponent } from '@elysian/ui';
import type { ImportMapping, ImportMappingColumns, InvoiceImportCheck, InvoiceImportLine } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { shortDate } from './shared';
import {
  columnLabel, DEFAULT_CREDIT_VALUES, DEFAULT_INVOICE_VALUES, doubledColumns, FIELDS, guessColumns, mappingProblem, missingHeadings,
  readFile, splitValues, toLines, type DateOrder,
} from './importInvoices';

const RouterLink = Link as unknown as LinkComponent;
const STATUS = {
  ok: { tone: 'ok', label: 'New draft' },
  skip: { tone: 'draft', label: 'Already in' },
  problem: { tone: 'warn', label: 'Problem' },
  missing_customer: { tone: 'warn', label: 'No customer' },
} as const;
const ORDERS: { key: DateOrder; label: string }[] = [
  { key: 'dmy', label: 'Day/month/year' },
  { key: 'mdy', label: 'Month/day/year' },
  { key: 'ymd', label: 'Year-month-day' },
];
const readText = (f: File) => (typeof f.text === 'function' ? f.text()
  : new Promise<string>((ok, no) => { const r = new FileReader(); r.onload = () => ok(String(r.result)); r.onerror = () => no(r.error); r.readAsText(f); }));
const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const money = (n: number | null | undefined) => (n === null || n === undefined ? '—' : <Money value={n.toFixed(2)} />);

/** A mapping as it's being made or changed (#48). */
type Draft = {
  id: string | null; name: string; columns: ImportMappingColumns; order: DateOrder; hasHeadings: boolean;
  invoiceValues: string; creditValues: string;
};
const draftFrom = (m: ImportMapping): Draft => ({
  id: m.id, name: m.name, columns: { ...m.columns }, order: (m.options?.date_order as DateOrder | undefined) ?? 'dmy',
  hasHeadings: m.options?.has_headings !== false,
  invoiceValues: (m.options?.invoice_values ?? DEFAULT_INVOICE_VALUES).join(', '),
  creditValues: (m.options?.credit_values ?? DEFAULT_CREDIT_VALUES).join(', '),
});

/**
 * #41, #44–#48: invoices and credit notes from another system's CSV. A saved mapping (one per system) says
 * which column holds what, whether the file has a heading row and what its Type column calls invoices and
 * credit notes. Every line is checked by the Ledger, customers not in the books are listed, and the invoices
 * come in as drafts keeping the other system's number, to review and post from the Invoices screen.
 */
export function ImportInvoices() {
  const { source, can } = useSession();
  const toast = useToast((t) => t.show);
  const navigate = useNavigate();
  const [version, setVersion] = useState(0);
  const saved = useAsync(() => source.invoiceMappings(), [source, version], { keep: true });
  const mappings = saved.state === 'ready' ? saved.data : [];
  const [mappingId, setMappingId] = useState('');
  const [editing, setEditing] = useState<Draft | null>(null);
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [checked, setChecked] = useState<{ lines: InvoiceImportLine[]; result: InvoiceImportCheck } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<ImportMapping | null>(null);
  const mayWrite = can('documents.write');

  const chosen = mappings.find((m) => m.id === mappingId) ?? null;
  // What reads the file: the mapping being edited, else the one chosen.
  const active: Draft | null = editing ?? (chosen ? draftFrom(chosen) : null);
  const read = useMemo(() => (file ? readFile(file.text, active?.hasHeadings ?? true) : null), [file, active?.hasHeadings]);
  const lines = useMemo(() => (read && active ? toLines(read, active.columns, active.order) : []), [read, active]);

  const choose = (id: string) => { setMappingId(id); setEditing(null); setChecked(null); setError(null); };
  const update = (patch: Partial<Draft>) => { setEditing((d) => (d ? { ...d, ...patch } : d)); setChecked(null); };

  function startCreate() {
    setChecked(null);
    setError(null);
    const r = file ? readFile(file.text, true) : null;
    setEditing({ id: null, name: '', columns: r ? guessColumns(r.headings) : {}, order: 'dmy', hasHeadings: true,
      invoiceValues: DEFAULT_INVOICE_VALUES.join(', '), creditValues: DEFAULT_CREDIT_VALUES.join(', ') });
  }

  async function chooseFile(f: File) {
    setError(null);
    setChecked(null);
    const text = await readText(f);
    if (!text.trim()) { setError('The file is empty.'); return; }
    setFile({ name: f.name, text });
    if (editing && !editing.id && !Object.keys(editing.columns).length) update({ columns: guessColumns(readFile(text, editing.hasHeadings).headings) });
  }

  async function saveMapping() {
    if (!editing) return;
    setError(null);
    try {
      const r = await source.saveInvoiceMapping({
        id: editing.id, name: editing.name.trim(), columns: editing.columns,
        options: { date_order: editing.order, has_headings: editing.hasHeadings,
          invoice_values: splitValues(editing.invoiceValues), credit_values: splitValues(editing.creditValues) },
      });
      setVersion((v) => v + 1);
      setMappingId(r.id);
      setEditing(null);
      toast(`Mapping “${editing.name.trim()}” saved`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function deleteMapping(m: ImportMapping) {
    setDeleting(null);
    try {
      await source.deleteInvoiceMapping(m.id);
      setVersion((v) => v + 1);
      choose('');
      toast(`Mapping “${m.name}” deleted`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const typeValues = () => (active?.columns.type
    ? { invoice: splitValues(active.invoiceValues), credit: splitValues(active.creditValues) } : undefined);

  async function check() {
    setBusy(true);
    setError(null);
    try {
      const result = await source.checkInvoiceImport({ source: active?.name.trim() || file?.name || 'CSV', apply: false, lines, type_values: typeValues() });
      setChecked({ lines, result });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function importDrafts() {
    if (!checked) return;
    setBusy(true);
    setError(null);
    try {
      const r = await source.checkInvoiceImport({ source: active?.name.trim() || file?.name || 'CSV', apply: true, lines: checked.lines, type_values: typeValues() });
      toast(`${count(r.imported, 'draft')} imported: review and post ${r.imported === 1 ? 'it' : 'them'} from Drafts`);
      navigate('/invoices');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const needs = active ? mappingProblem(active.columns) : null;
  const notInFile = read && active && !editing ? missingHeadings(active.columns, read.headings) : [];
  const doubled = editing ? doubledColumns(editing.columns) : {};
  const both = editing ? splitValues(editing.invoiceValues).filter((v) => splitValues(editing.creditValues).some((c) => c.toLowerCase() === v.toLowerCase())) : [];
  const r = checked?.result;
  const toImport = r ? r.documents.filter((d) => d.status === 'ok').length : 0;
  const problemRows = r ? r.rows.filter((x) => x.status === 'problem') : [];
  const ready = r && r.counts.problems === 0 && r.missing_customers.length === 0 && toImport > 0;
  const canCheck = !editing && active && read && !needs && lines.length > 0 && mayWrite;

  return (
    <>
      <PageHeader
        title="Import invoices"
        subtitle="Invoices and credit notes from another system’s CSV. They come in as drafts, keeping their own numbers, for you to review and post."
        actions={<ButtonLink as={RouterLink} to="/invoices" variant="ghost">Back to invoices</ButtonLink>}
      />
      {!mayWrite && <Banner>You can look, but importing invoices needs permission to write documents.</Banner>}
      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(0, 1fr)' }}>
        <Card as="section" padding="18px 16px" aria-labelledby="ii-file">
          <h2 id="ii-file" className="el-h2" style={{ margin: '0 0 12px' }}>1. The file</h2>
          <div className="el-import-mapping">
            <Field id="ii-mapping" label="Mapping" hint={mappings.length ? 'How this system’s files are laid out' : 'No mappings yet: create one for the system the file comes from'}>
              <select id="ii-mapping" className="el-input" value={mappingId} disabled={Boolean(editing)} onChange={(e) => choose(e.target.value)}>
                <option value="">{mappings.length ? 'Choose a mapping…' : '(none yet)'}</option>
                {mappings.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </Field>
            <div className="el-row el-import-mapping-actions">
              <Button disabled={!mayWrite || Boolean(editing)} onClick={startCreate}>Create mapping</Button>
              <Button disabled={!mayWrite || !chosen || Boolean(editing)} onClick={() => { setChecked(null); setEditing(draftFrom(chosen!)); }}>Edit mapping</Button>
              <Button variant="danger" disabled={!mayWrite || !chosen || Boolean(editing)} onClick={() => setDeleting(chosen)}>Delete mapping</Button>
            </div>
          </div>
          <FileDrop label="Choose the invoice CSV" accept=".csv,text/csv" disabled={busy || !mayWrite}
            hint={file ? `${file.name}${active ? `: ${count(lines.length, 'line')} to check` : ''}. Nothing is imported yet.` : 'One line per invoice line. Nothing is imported yet.'}
            onFiles={(files) => files[0] && void chooseFile(files[0])} />
          {notInFile.length > 0 && (
            <Banner>This file has no column called {notInFile.map((h) => `“${h}”`).join(', ')}, which “{chosen?.name}” uses. Edit the mapping, or check it’s the right file.</Banner>
          )}
          {!editing && (
            <div className="el-row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
              {!active && file && <span className="el-hint" style={{ marginRight: 'auto' }}>Choose or create a mapping to read the file.</span>}
              <Button variant="primary" disabled={busy || !canCheck} onClick={() => void check()}>
                {busy && !checked ? 'Checking…' : `Check ${count(lines.length, 'line')}`}
              </Button>
            </div>
          )}
          {error && !checked && !editing && <div className="el-error-box" role="alert">{error}</div>}
        </Card>

        {editing && (
          <Card as="section" padding="18px 16px" aria-labelledby="ii-columns">
            <h2 id="ii-columns" className="el-h2" style={{ margin: '0 0 12px' }}>2. {editing.id ? `Edit “${chosen?.name ?? editing.name}”` : 'Create a mapping'}</h2>
            <div className="el-import-settings">
              <Field id="ii-name" label="Mapping name" hint="The system the file comes from">
                <input id="ii-name" className="el-input" value={editing.name} maxLength={60} placeholder="Such as Old Billing" onChange={(e) => update({ name: e.target.value })} />
              </Field>
              <Field id="ii-order" label="Dates are written" hint="05/10/2026 is 5 October as day/month/year">
                <select id="ii-order" className="el-input" value={editing.order} onChange={(e) => update({ order: e.target.value as DateOrder })}>
                  {ORDERS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </Field>
              <label className="el-check el-import-headings">
                <input type="checkbox" checked={editing.hasHeadings}
                  onChange={(e) => update({ hasHeadings: e.target.checked, columns: file ? guessColumns(readFile(file.text, e.target.checked).headings) : {} })} />
                The first line holds headings (untick if it’s already an invoice)
              </label>
            </div>

            <h3 className="el-h3" style={{ margin: '18px 0 4px' }}>Which column holds what</h3>
            {!read && <p className="el-hint" style={{ margin: 0 }}>Choose the file above to see its columns.</p>}
            {read && (
              <ol className="el-import-columns" aria-label="Columns">
                {FIELDS.map((f) => (
                  <li key={f.key}>
                    <label htmlFor={`ii-col-${f.key}`} className="el-import-field">
                      <span>{f.label}{f.required && <span aria-hidden="true"> *</span>}</span>
                      {f.hint && <span className="el-hint">{f.hint}</span>}
                    </label>
                    <div>
                      <select id={`ii-col-${f.key}`} className="el-input" value={editing.columns[f.key] ?? ''}
                        aria-describedby={doubled[f.key] ? `ii-dbl-${f.key}` : undefined}
                        onChange={(e) => update({ columns: (() => { const n = { ...editing.columns }; if (e.target.value) n[f.key] = e.target.value; else delete n[f.key]; return n; })() })}>
                        <option value="">— Not in the file —</option>
                        {read.headings.map((h, i) => (h ? <option key={i} value={h}>{columnLabel(read, i)}</option> : null))}
                      </select>
                      {doubled[f.key] && <div id={`ii-dbl-${f.key}`} className="el-error-text">Same column as {doubled[f.key]!.join(', ')}</div>}
                    </div>
                  </li>
                ))}
              </ol>
            )}

            {editing.columns.type && (
              <div className="el-import-settings" style={{ marginTop: 12 }}>
                <Field id="ii-inv-values" label="Type values for an invoice" hint="Separate with commas, such as SI, Invoice">
                  <input id="ii-inv-values" className="el-input" value={editing.invoiceValues} onChange={(e) => update({ invoiceValues: e.target.value })} />
                </Field>
                <Field id="ii-cred-values" label="Type values for a credit note" hint="Any other type is shown as a problem">
                  <input id="ii-cred-values" className="el-input" value={editing.creditValues} onChange={(e) => update({ creditValues: e.target.value })} />
                </Field>
              </div>
            )}
            {both.length > 0 && <div className="el-error-text" role="alert">{both.join(', ')} can’t mean both an invoice and a credit note.</div>}
            {needs && <p className="el-hint" role="status" style={{ margin: '10px 0 0' }}>{needs}.</p>}
            {error && <div className="el-error-box" role="alert">{error}</div>}
            <div className="el-row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <Button variant="ghost" onClick={() => { setEditing(null); setError(null); }}>Cancel</Button>
              <Button variant="primary" disabled={!editing.name.trim() || Boolean(needs) || both.length > 0 || Object.keys(doubled).length > 0 || !mayWrite}
                onClick={() => void saveMapping()}>Save mapping</Button>
            </div>
          </Card>
        )}

        {r && !editing && (
          <Card as="section" padding="18px 0 8px" aria-labelledby="ii-check">
            <div className="el-card-head el-txn-head">
              <div>
                <h2 id="ii-check" className="el-h2">2. Check and import</h2>
                <div className="el-hint">
                  {count(r.counts.invoices, 'invoice')} and {count(r.counts.credit_notes, 'credit note')} from {count(r.counts.lines, 'line')}:
                  net <Money value={r.totals.net.toFixed(2)} />, VAT <Money value={r.totals.vat.toFixed(2)} />
                  {r.counts.skipped > 0 && `; ${count(r.counts.skipped, 'line')} already in the Ledger, left out`}.
                </div>
              </div>
              <Button variant="primary" disabled={busy || !ready || !mayWrite} onClick={() => void importDrafts()}>
                {busy ? 'Importing…' : `Import ${count(toImport, 'draft')}`}
              </Button>
            </div>
            <div style={{ padding: '0 16px' }}>
              {r.missing_customers.length > 0 && (
                <div className="el-error-box" role="alert">
                  <strong>{count(r.missing_customers.length, 'customer is', 'customers are')} not in the Ledger</strong>, so nothing can be imported yet.
                  Add {r.missing_customers.length === 1 ? 'it' : 'them'} under Customers (with the same account code), or import them first, then Check again.
                  <ul aria-label="Missing customers" style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {r.missing_customers.map((m) => <li key={m.code}>{m.code}{m.name ? ` · ${m.name}` : ''} <span className="el-hint">(row{m.rows.length === 1 ? '' : 's'} {m.rows.join(', ')})</span></li>)}
                  </ul>
                </div>
              )}
              {problemRows.length > 0 && (
                <div className="el-error-box" role="alert">
                  {count(problemRows.length, 'line needs', 'lines need')} attention: put {problemRows.length === 1 ? 'it' : 'them'} right in the file, the mapping or the Ledger, and check again.
                </div>
              )}
              {ready && <Banner tone="info">Everything checks out. Importing makes {count(toImport, 'draft')}; nothing is posted until you approve {toImport === 1 ? 'it' : 'them'}.</Banner>}
              {error && <div className="el-error-box" role="alert">{error}</div>}
            </div>
            {problemRows.length > 0 && (
              <div className="el-table-wrap">
                <table className="el-table" aria-label="Lines that need attention">
                  <thead><tr><th scope="col">Row</th><th scope="col">Number</th><th scope="col">Customer</th><th scope="col">What’s wrong</th></tr></thead>
                  <tbody>
                    {problemRows.map((x) => (
                      <tr key={x.row_no}>
                        <td className="el-muted">{x.row_no}</td>
                        <td>{x.number ?? <span className="el-muted">(none)</span>}</td>
                        <td>{x.customer ?? x.customer_code ?? <span className="el-muted">(none)</span>}</td>
                        <td>{x.problems.join('. ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="el-table-wrap">
              <table className="el-table" aria-label="Invoices to import">
                <thead>
                  <tr><th scope="col">Number</th><th scope="col">Customer</th><th scope="col">Date</th><th scope="col" className="el-num">Lines</th>
                    <th scope="col" className="el-num">Net</th><th scope="col" className="el-num">VAT</th><th scope="col" className="el-num">Gross</th><th scope="col">Will be</th></tr>
                </thead>
                <tbody>
                  {r.documents.map((d) => (
                    <tr key={`${d.type}-${d.number}`} className={d.status === 'skip' ? 'el-row-off' : undefined}>
                      <td style={{ whiteSpace: 'nowrap' }}>{d.number}{d.type === 'sales_credit' && <div className="el-hint">Credit note</div>}</td>
                      <td>{d.customer ?? d.customer_code}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{d.date ? shortDate(d.date) : '—'}</td>
                      <td className="el-num">{d.lines}</td>
                      <td className="el-num">{money(d.net)}</td>
                      <td className="el-num">{money(d.vat)}</td>
                      <td className="el-num">{money(d.gross)}</td>
                      <td><Pill tone={STATUS[d.status].tone}>{STATUS[d.status].label}</Pill></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {deleting && (
        <Dialog title={`Delete “${deleting.name}”?`} onClose={() => setDeleting(null)}
          actions={<>
            <Button variant="ghost" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button variant="danger" onClick={() => void deleteMapping(deleting)}>Delete mapping</Button>
          </>}>
          <p style={{ margin: 0 }}>Only the saved columns go; invoices already imported with it stay as they are.</p>
        </Dialog>
      )}
    </>
  );
}
